import React, { useEffect, useMemo, useState } from "react";
import DocumentIcon from "../../icons/Arrows/DocumentIcon";
import DownloadIcon from "../../icons/DownloadIcon";
import companyController from "../../controllers/companyController";
import { userService } from "../../services/userService";
import "../../styles/VendorReviewView.css";
import { translateLabel, useTranslationSnapshot } from "./../../translations/runtime";


const displayValue = (value) =>
  value === undefined || value === null || value === "" ? "-" : String(value);

const isPresent = (value) =>
  value !== undefined && value !== null && value !== "";

const firstDefined = (...values) => values.find(isPresent) || "";

const ATTACHMENT_STATUS_OPTIONS = [
  "PENDING",
  "UNCLEAR_BLURRY",
  "EXPIRED_DOCUMENT",
  "WRONG_DOCUMENT_TYPE",
  "INFORMATION_MISMATCH",
  "INCOMPLETE_INFORMATION",
  "APPROVED",
  "VERIFIED",
  "DEFAULT",
];

const getAttachmentId = (document = {}) =>
  firstDefined(
    document.attachmentID,
    document.attachmentId,
    document.attachment_id,
    document.id,
  );

const getStoredToken = () => {
  const storedUser = userService.getJwt();

  if (typeof storedUser === "string") return storedUser;

  return (
    storedUser?.jwt ||
    storedUser?.token ||
    storedUser?.accessToken ||
    storedUser?.access_token ||
    ""
  );
};

const formatPhone = (phone, code) => {
  const phoneText = String(phone || "").trim();
  const codeText = String(code || "").trim();

  if (!phoneText) return "";
  if (!codeText || phoneText.startsWith("+")) return phoneText;

  const prefix = codeText.startsWith("+") ? codeText : `+${codeText}`;
  return `${prefix} ${phoneText}`;
};

const PHONE_KEY_PATTERN =
  /(phone|mobile|contactnumber|contactno|tel|telephone|whatsapp)/i;
const PHONE_CODE_KEY_PATTERN =
  /(countrycode|phonecode|dialcode|prefix|suffix)/i;

const findPhoneInObject = (source, depth = 0) => {
  if (!source || typeof source !== "object" || depth > 3) return "";

  for (const [key, value] of Object.entries(source)) {
    if (!isPresent(value)) continue;

    const normalizedKey = key.replace(/[^a-z0-9]/gi, "").toLowerCase();

    if (
      PHONE_KEY_PATTERN.test(normalizedKey) &&
      !PHONE_CODE_KEY_PATTERN.test(normalizedKey) &&
      (typeof value === "string" || typeof value === "number")
    ) {
      return String(value).trim();
    }
  }

  for (const value of Object.values(source)) {
    if (value && typeof value === "object") {
      const phone = findPhoneInObject(value, depth + 1);
      if (phone) return phone;
    }
  }

  return "";
};

const toDisplayLabel = (value) => {
  if (!value) return "-";

  return String(value)
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const getDocumentHref = (dataUrl) => {
  if (!dataUrl) return "";
  if (/^(data:|https?:|blob:)/i.test(dataUrl)) return dataUrl;
  return `data:application/octet-stream;base64,${dataUrl}`;
};

function VendorReviewDetails({ row = {}, onAttachmentReasonsChange }) {
  useTranslationSnapshot();
  const [documentStatuses, setDocumentStatuses] = useState({});
  const [savingDocumentStatus, setSavingDocumentStatus] = useState({});
  const [documentStatusErrors, setDocumentStatusErrors] = useState({});
  const company = row.company || {};
  const companyDetails = row.companyDetails || {};
  const businessProfile = row.businessProfile || {};
  const owner = row.owner || row.applicant || company.owner || {};
  const bank = row.bank || row.bankingDetails || row.bankDetails || {};
  const storeName =
    firstDefined(
      row.companyName,
      row.storeName,
      company.companyName,
      companyDetails.companyName,
      row.businessName,
    ) || "Unknown store";
  const documentsSource = firstDefined(
    row.documentationVerification,
    row.documents,
    row.companyAttachments,
    row.attachments,
    company.documentationVerification,
    company.attachments,
    companyDetails.documentationVerification,
    companyDetails.attachments,
  );
  const documents = useMemo(
    () => (Array.isArray(documentsSource) ? documentsSource : []),
    [documentsSource],
  );

  const buildAttachmentReasons = (statusOverrides = {}) =>
    documents.map((document, index) => {
      const attachmentID = getAttachmentId(document);
      const statusKey = attachmentID || `document-${index}`;

      return {
        attachmentID,
        attachmentStatus: firstDefined(
          statusOverrides[statusKey],
          documentStatuses[statusKey],
          document.attachmentStatus,
          "DEFAULT",
        ),
        attachmentType: document.attachmentType,
        documentName: toDisplayLabel(document.attachmentType),
      };
    });

  useEffect(() => {
    const nextStatuses = documents.reduce((statuses, document, index) => {
      const attachmentId = getAttachmentId(document) || `document-${index}`;

      statuses[attachmentId] = firstDefined(
        document.attachmentStatus,
        "DEFAULT",
      );

      return statuses;
    }, {});

    setDocumentStatuses(nextStatuses);
    onAttachmentReasonsChange?.(
      documents.map((document, index) => {
        const attachmentID = getAttachmentId(document);
        const statusKey = attachmentID || `document-${index}`;

        return {
          attachmentID,
          attachmentStatus: nextStatuses[statusKey],
          attachmentType: document.attachmentType,
          documentName: toDisplayLabel(document.attachmentType),
        };
      }),
    );
    setSavingDocumentStatus({});
    setDocumentStatusErrors({});
  }, [documents, onAttachmentReasonsChange]);

  const handleAttachmentStatusChange = async (document, index, nextStatus) => {
    const attachmentID = getAttachmentId(document);
    const statusKey = attachmentID || `document-${index}`;
    const previousStatus = firstDefined(
      documentStatuses[statusKey],
      document.attachmentStatus,
      "DEFAULT",
    );

    if (!attachmentID) {
      setDocumentStatusErrors((currentErrors) => ({
        ...currentErrors,
        [statusKey]: "Attachment ID is missing.",
      }));
      return;
    }

    setDocumentStatuses((currentStatuses) => ({
      ...currentStatuses,
      [statusKey]: nextStatus,
    }));
    setSavingDocumentStatus((currentSaving) => ({
      ...currentSaving,
      [statusKey]: true,
    }));
    setDocumentStatusErrors((currentErrors) => ({
      ...currentErrors,
      [statusKey]: "",
    }));

    onAttachmentReasonsChange?.(buildAttachmentReasons({ [statusKey]: nextStatus }));

    try {
      const response = await companyController.updateCompanyAttachmentStatus({
        attachments: {
          attachmentID,
          attachmentStatus: nextStatus,
        },
        jwt: getStoredToken(),
      });

      if (
        response instanceof Error ||
        response?.isAxiosError ||
        response?.response
      ) {
        throw response;
      }
    } catch (requestError) {
      setDocumentStatuses((currentStatuses) => ({
        ...currentStatuses,
        [statusKey]: previousStatus,
      }));
      onAttachmentReasonsChange?.(
        buildAttachmentReasons({ [statusKey]: previousStatus }),
      );
      setDocumentStatusErrors((currentErrors) => ({
        ...currentErrors,
        [statusKey]:
          requestError?.response?.data?.message ||
          requestError?.message ||
          "Unable to update document status.",
      }));
    } finally {
      setSavingDocumentStatus((currentSaving) => ({
        ...currentSaving,
        [statusKey]: false,
      }));
    }
  };
  const description = firstDefined(
    row.description,
    row.companyDescription,
    company.description,
    company.companyDescription,
    companyDetails.description,
    companyDetails.companyDescription,
  );
  const category = firstDefined(
    row.category,
    row.businessCategory,
    row.businessType,
    businessProfile.businessProfileCategoryName,
    businessProfile.categoryName,
    businessProfile.name,
    company.businessType,
    companyDetails.businessType,
  );
  const legalBusinessName = firstDefined(
    row.legalBusinessName,
    row.businessLegalName,
    row.companyLegalName,
    row.companyName,
    company.legalBusinessName,
    company.businessLegalName,
    company.companyLegalName,
    company.companyName,
    companyDetails.legalBusinessName,
    companyDetails.businessLegalName,
    companyDetails.companyLegalName,
    companyDetails.companyName,
  );
  const businessType = firstDefined(
    row.businessType,
    row.companyType,
    company.businessType,
    company.companyType,
    companyDetails.businessType,
    companyDetails.companyType,
    businessProfile.businessProfileTypeName,
    businessProfile.typeName,
  );
  const taxNumber = firstDefined(
    row.taxNumber,
    row.taxID,
    row.taxId,
    company.taxNumber,
    company.taxID,
    company.taxId,
    companyDetails.taxNumber,
    companyDetails.taxID,
    companyDetails.taxId,
  );
  const isTaxNumberVerified = firstDefined(
    row.isTaxNumberVerified,
    row.taxNumberVerified,
    company.isTaxNumberVerified,
    company.taxNumberVerified,
    companyDetails.isTaxNumberVerified,
    companyDetails.taxNumberVerified,
  );
  const registrationNumber = firstDefined(
    row.registrationNumber,
    row.registrationNb,
    row.commercialRegistrationNumber,
    company.registrationNumber,
    company.registrationNb,
    company.commercialRegistrationNumber,
    companyDetails.registrationNumber,
    companyDetails.registrationNb,
    companyDetails.commercialRegistrationNumber,
  );
  const countryName = firstDefined(
    row.countryName,
    row.country,
    company.countryName,
    company.country,
    companyDetails.countryName,
    companyDetails.country,
  );
  const ownerName = firstDefined(
    row.ownerName,
    row.applicantName,
    row.fullName,
    owner.fullName,
    owner.name,
    owner.ownerName,
  );
  const ownerId = firstDefined(
    row.ownerUserId,
    row.ownerId,
    row.ownerID,
    row.userId,
    row.userID,
    owner.userId,
    owner.userID,
    owner.id,
  );
  const emailAddress = firstDefined(
    row.vendorEmail,
    row.vendorEmailAddress,
    row.ownerEmail,
    row.ownerEmailAddress,
    row.applicantEmail,
    row.applicantEmailAddress,
    row.contactEmail,
    row.contactEmailAddress,
    row.companyEmail,
    row.businessEmail,
    company.vendorEmail,
    company.vendorEmailAddress,
    company.ownerEmail,
    company.ownerEmailAddress,
    company.emailAddress,
    company.email,
    row.emailAddress,
    row.email,
    owner.emailAddress,
    owner.email,
  );
  const phoneNumber = firstDefined(
    formatPhone(row.phoneNumber, row.suffix),
    formatPhone(companyDetails.phoneNumber, companyDetails.suffix),
    row.phoneNumber,
    companyDetails.phoneNumber,
    row.registrationDTO?.phoneNumber,
    companyDetails.registrationDTO?.phoneNumber,
    row.registeredUser?.phoneNumber,
    companyDetails.registeredUser?.phoneNumber,
    row.user?.phoneNumber,
    companyDetails.user?.phoneNumber,
    row.userDetails?.phoneNumber,
    companyDetails.userDetails?.phoneNumber,
    row.companyDetails?.phoneNumber,
    companyDetails.companyDetails?.phoneNumber,
    row.applicant?.phoneNumber,
    companyDetails.applicant?.phoneNumber,
    formatPhone(
      row.registrationDTO?.phoneNumber,
      row.registrationDTO?.phoneCountryCode,
    ),
    formatPhone(
      companyDetails.registrationDTO?.phoneNumber,
      companyDetails.registrationDTO?.phoneCountryCode,
    ),
    company.phoneNumber,
    owner.phoneNumber,
    formatPhone(row.vendorPhoneNumber, row.vendorPhoneCountryCode),
    formatPhone(row.vendorPhone, row.vendorPhoneCountryCode),
    row.vendorMobileNumber,
    row.vendorMobile,
    row.ownerPhoneNumber,
    row.ownerPhone,
    row.ownerMobileNumber,
    row.ownerMobile,
    row.ownerPhoneNb,
    row.ownerPhoneNo,
    row.applicantPhoneNumber,
    row.applicantPhone,
    row.applicantMobileNumber,
    row.applicantMobile,
    row.applicantPhoneNb,
    row.applicantPhoneNo,
    row.contactPhoneNumber,
    row.contactPhone,
    row.contactNumber,
    row.contactNo,
    row.mobileNumber,
    row.mobile,
    row.phoneNb,
    row.phoneNo,
    row.telephone,
    row.phone,
    formatPhone(company.phoneNumber, company.phoneCountryCode),
    company.ownerPhoneNumber,
    company.ownerPhone,
    company.contactPhoneNumber,
    company.contactNumber,
    company.mobileNumber,
    owner.phone,
    owner.mobileNumber,
    owner.mobile,
    findPhoneInObject(row),
  );
  const bankName = firstDefined(row.bankName, bank.bankName, bank.name);
  const accountHolder = firstDefined(
    row.accountHolder,
    row.accountHolderName,
    bank.accountHolder,
    bank.accountHolderName,
  );
  const accountNumber = firstDefined(
    row.accountNumber,
    row.iban,
    bank.accountNumber,
    bank.iban,
  );

  return (
    <div className="vendor-review-view-left">
      <div className="vendor-review-view-section">
        <div className="vendor-review-view-field">
          <div className="vendor-review-view-field-label">{translateLabel("input_store_name", "Store Name")}</div>
          <div className="vendor-review-view-field-value">{storeName}</div>
        </div>
        <div className="vendor-review-view-field">
          <div className="vendor-review-view-field-sublabel">{translateLabel("input_business_description", "Description")}</div>
          <div className="vendor-review-view-field-value">
            {displayValue(description)}
          </div>
        </div>
        <div className="vendor-review-view-field">
          <div className="vendor-review-view-field-sublabel">
            Business Category
          </div>
          <div className="vendor-review-view-field-value">
            {toDisplayLabel(category)}
          </div>
        </div>
      </div>

      <div className="vendor-review-view-section">
        <div className="vendor-review-view-section-title">Business Details</div>
        <div className="vendor-review-view-grid-2">
          <div className="vendor-review-view-field">
            <div className="vendor-review-view-field-sublabel">
              Legal Business Name
            </div>
            <div className="vendor-review-view-field-value">
              {displayValue(legalBusinessName)}
            </div>
          </div>
          <div className="vendor-review-view-field">
            <div className="vendor-review-view-field-sublabel"> {translateLabel("input_business_type", "Business Type")} </div>
            <div className="vendor-review-view-field-value">
              {toDisplayLabel(businessType)}
            </div>
          </div>
          <div className="vendor-review-view-field">
            <div className="vendor-review-view-field-sublabel">{translateLabel("input_tax_number", "Tax Number")}</div>
            <div className="vendor-review-view-field-value">
              {displayValue(taxNumber)}
            </div>
          </div>
          <div className="vendor-review-view-field">
            <div className="vendor-review-view-field-sublabel">
              Tax Verification
            </div>
            <div className="vendor-review-view-field-value">
              {isTaxNumberVerified ? translateLabel("txt_admin_verified", "Verified") : translateLabel("txt_admin_not_verified", "Not verified")}
            </div>
          </div>
          <div className="vendor-review-view-field">
            <div className="vendor-review-view-field-sublabel">
              Registration Number
            </div>
            <div className="vendor-review-view-field-value">
              {displayValue(registrationNumber)}
            </div>
          </div>
          <div className="vendor-review-view-field">
            <div className="vendor-review-view-field-sublabel">Country</div>
            <div className="vendor-review-view-field-value">
              {displayValue(countryName)}
            </div>
          </div>
        </div>
      </div>

      <div className="vendor-review-view-section-owner">
        <div className="vendor-review-view-section-title">
          Owner Information
        </div>
        <div className="vendor-review-view-grid-2">
          <div className="vendor-review-view-field">
            <div className="vendor-review-view-field-sublabel">{translateLabel("txt_common_full_name", "Full Name")}</div>
            <div className="vendor-review-view-field-value">
              {displayValue(ownerName)}
            </div>
          </div>
          <div className="vendor-review-view-field">
            <div className="vendor-review-view-field-sublabel">Owner ID</div>
            <div className="vendor-review-view-field-value">
              {displayValue(ownerId)}
            </div>
          </div>
          <div className="vendor-review-view-field">
            <div className="vendor-review-view-field-sublabel">{translateLabel("txt_end_user_email", "Email")}</div>
            <div className="vendor-review-view-field-value">
              {displayValue(emailAddress)}
            </div>
          </div>
          <div className="vendor-review-view-field">
            <div className="vendor-review-view-field-sublabel">Phone</div>
            <div className="vendor-review-view-field-value">
              {displayValue(phoneNumber)}
            </div>
          </div>
        </div>
      </div>

      {/* <div className="vendor-review-view-section-banking">
        <div className="vendor-review-view-section-title">Banking Details</div>
        <div className="vendor-review-view-grid-2">
          <div className="vendor-review-view-field">
            <div className="vendor-review-view-field-sublabel">Bank Name</div>
            <div className="vendor-review-view-field-value">
              {displayValue(bankName)}
            </div>
          </div>
          <div className="vendor-review-view-field">
            <div className="vendor-review-view-field-sublabel">
              Account Holder
            </div>
            <div className="vendor-review-view-field-value">
              {displayValue(accountHolder)}
            </div>
          </div>
        </div>
        <div className="vendor-review-view-field">
          <div className="vendor-review-view-field-sublabel">
            Account Number
          </div>
          <div className="vendor-review-view-field-value">
            {displayValue(accountNumber)}
          </div>
        </div>
      </div> */}

      <div className="vendor-review-view-section-documents">
        <div className="vendor-review-view-section-title">
          Submitted Documents
        </div>
        <div className="vendor-review-view-docs-count">
          {documents.length} document(s) uploaded
        </div>

        {documents.length === 0 && (
          <div className="vendor-review-view-section-value">
            No documents submitted.
          </div>
        )}

        {documents.map((document, index) => {
          const documentName = toDisplayLabel(document.attachmentType);
          const documentHref = getDocumentHref(document.dataUrl);
          const attachmentId = getAttachmentId(document);
          const statusKey = attachmentId || `document-${index}`;
          const selectedStatus = firstDefined(
            documentStatuses[statusKey],
            document.attachmentStatus,
            "DEFAULT",
          );
          const isSavingStatus = Boolean(savingDocumentStatus[statusKey]);
          const statusError = documentStatusErrors[statusKey];

          return (
            <div
              className="vendor-review-view-doc-row"
              key={document.attachmentID || `${documentName}-${index}`}
            >
              <div className="vendor-review-view-doc-info">
                <DocumentIcon />
                <div>
                  <div className="vendor-review-view-doc-name">
                    {documentName}
                  </div>
                  <div className="vendor-review-view-doc-date">
                    Status: {toDisplayLabel(selectedStatus)}
                  </div>
                  {statusError && (
                    <div className="vendor-review-view-doc-status-error">
                      {statusError}
                    </div>
                  )}
                </div>
              </div>

              <div className="vendor-review-view-doc-actions">
                <select
                  className="vendor-review-view-doc-status-select"
                  disabled={isSavingStatus}
                  value={selectedStatus}
                  onChange={(event) =>
                    handleAttachmentStatusChange(
                      document,
                      index,
                      event.target.value,
                    )
                  }
                >
                  {ATTACHMENT_STATUS_OPTIONS.map((statusOption) => (
                    <option key={statusOption} value={statusOption}>
                      {toDisplayLabel(statusOption)}
                    </option>
                  ))}
                </select>

                {documentHref ? (
                  <a
                    className="vendor-review-view-download-btn"
                    href={documentHref}
                    download={document.attachmentType || "document"}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <DownloadIcon />
                    <span className="vendor-review-view-download-text"> {translateLabel("btn_download", "Download")} </span>
                  </a>
                ) : (
                  <span className="vendor-review-view-download-btn vendor-review-view-download-btn-disabled">
                    <DownloadIcon />
                    <span className="vendor-review-view-download-text"> {translateLabel("txt_business_unavailable", "Unavailable")} </span>
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default VendorReviewDetails;
